// © 2025 Ananda Kumar. All rights reserved.
// Unauthorized copying of this file, via any medium, is strictly prohibited.

import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, ElementRef, HostListener, inject, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../services/auth.service';
import { SidebarService } from '../../services/sidebar.service';
import { Observable } from 'rxjs';
import { delay } from 'rxjs/operators';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { NavigationConfig } from '../models/navigationConfig.model';
import { MenuConfigItem } from '../models/domain.models';
import { environment } from '../../environments/environment';
import { TenantService } from '../../services/tenant.service';
import { ConfigurationService } from '../../services/configuration.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './side-bar.component.html',
  styleUrls: ['./side-bar.component.css'],
  standalone: false
})
export class SideBarComponent {
  private destroyRef = inject(DestroyRef);
  activeMenu: string | null = null;
  showSideBar$!: Observable<boolean>;
  isLoggedIn$!: Observable<boolean>;
  isAdmin$!: Observable<boolean>;
  showUpdates: boolean = false;
  showEventUpdates: boolean = false;

  navigationConfig: NavigationConfig = new NavigationConfig();

  get exclusiveConfig() {
    return this.navigationConfig.exclusiveCard;
  }

  menuItems = [
    { id: 1, label: 'Home', routeUrl: "", isExclusive: false, image: "icons/home.svg" },
  ];

  hoveredItem: number | null = null;
  userRoles: string[] = [];
  trackById = (_: number, item: any) => item.id;
  navigateTo: string = '';
  eventOverview: any;
  title$!: Observable<string>;
  menuConfig$: Observable<any>;

  constructor(
    private authService: AuthService,
    private http: HttpClient,
    private router: Router,
    public tenantService: TenantService,
    private configurationService: ConfigurationService,
    public sideBarService: SidebarService) {
    this.isLoggedIn$ = this.authService.isLoggedIn$;
    this.isAdmin$ = this.authService.isAdmin$;
    this.showSideBar$ = this.sideBarService.show$;
    this.title$ = this.sideBarService.title$.pipe(delay(0));
    this.menuConfig$ = this.configurationService.getMenu();
  }

  ngOnInit() {
    this.loadMenuItems();


    this.userRoles = this.authService.getUserRoles();

    this.sideBarService.navigateTo$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (data: string) => {
        this.navigateTo = data;
      }
    });

    // Use configuration-provided overview; assets were removed
    this.configurationService.getConfiguration().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(config => {
      const eoRaw = config?.['eventOverview'] ?? null;
      let eo: any = null;
      if (eoRaw) {
        try {
          eo = typeof eoRaw === 'string' ? JSON.parse(eoRaw) : eoRaw;
        } catch {
          eo = eoRaw;
        }
      }
      // also support top-level fields if eventOverview not present
      if (!eo) {
        eo = {
          whatsappChannelUrl: config?.['whatsappChannelUrl'] ?? config?.['whatsappChannel'] ?? null
        };
      }
      this.eventOverview = eo;
    });
  }

  get tenantId() {
    return this.tenantService.getTenantId();
  }

  loadMenuItems() {
    this.menuConfig$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (data: MenuConfigItem[]) => {
        const isActive = (key: string) => data.some(x => x.menu === key && x.isActive);
        const hasLabel = (label: string) => this.menuItems.some(x => x.label === label);

        if (isActive('maps') && !hasLabel('Maps')) {
          this.menuItems.push({ id: 3, label: 'Maps', routeUrl: 'event/maps', isExclusive: false, image: 'icons/nav-pin.svg' });
        }
        if (isActive('gallery') && !hasLabel('Gallery')) {
          this.menuItems.push({ id: 2, label: 'Gallery', routeUrl: 'gallery', isExclusive: false, image: 'icons/collection.svg' });
        }
        if (isActive('eventOrganisers') && !hasLabel('Event Organizers')) {
          this.menuItems.push({ id: 3, label: 'Event Organizers', routeUrl: 'event/eventOrganizers', isExclusive: false, image: 'icons/person-identity.svg' });
        }
        if (isActive('accommodation') && !hasLabel('Accommodation')) {
          this.menuItems.push({ id: 4, label: 'Accommodation', routeUrl: 'guests/accommodation', isExclusive: true, image: 'icons/person-out.svg' });
        }
        if (isActive('shareRide') && !hasLabel('Share the Ride')) {
          this.menuItems.push({ id: 5, label: 'Share the Ride', routeUrl: 'planJourney', isExclusive: false, image: 'icons/share.svg' });
        }
        if (isActive('photoBooth') && !hasLabel('Photo Booth')) {
          this.menuItems.push({ id: 5, label: 'Photo Booth', routeUrl: 'event/photoBooth', isExclusive: false, image: 'icons/camera-outline.svg' });
        }
        if (isActive('eventUpdates') && !hasLabel('Event Updates')) {
          this.showEventUpdates = true;
          this.menuItems.push({ id: 5, label: 'Event Updates', routeUrl: 'event/updates', isExclusive: false, image: 'icons/notification-unread-grey.svg' });
        }
        if (isActive('transportOptions') && !hasLabel('Transport Options')) {
          this.menuItems.push({ id: 6, label: 'Transport Options', routeUrl: 'howToReach', isExclusive: false, image: 'icons/nav-pin.svg' });
        }
        if (isActive('placesToVisit') && !hasLabel('Places to Visit')) {
          this.menuItems.push({ id: 7, label: 'Places to Visit', routeUrl: 'thingsToDo', isExclusive: false, image: 'icons/location-pin.svg' });
        }
        if (isActive('viewInvite') && !hasLabel('View Invite')) {
          this.menuItems.push({ id: 7, label: 'View Invite', routeUrl: 'event/invitation', isExclusive: false, image: 'icons/book.svg' });
        }
        if (isActive('showUpdates')) {
          this.showUpdates = true;
        }
      }
    });
  }

  showExclusiveCard(): boolean {
    return this.exclusiveConfig.show &&
      (this.exclusiveConfig.rolesRequired.length === 0 ||
        this.exclusiveConfig.rolesRequired.some(role => this.userRoles.includes(role) || this.userRoles.includes('admin')));
  }

  toggleMenu(menu: string) {
    this.activeMenu = this.activeMenu === menu ? null : menu;
  }

  showMenu(id: number) {
    this.hoveredItem = id;
  }

  hideMenu() {
    this.hoveredItem = null;
  }

  logout() {
    this.authService.logout().subscribe({
      next: () => {
        // Logout successful, redirect to tenant home
        const tenantKey = this.tenantService.getTenantKey();
        this.router.navigate([`/${tenantKey}`]);
      },
      error: (error) => {
        // Even if logout fails, redirect to tenant home
        console.error('Logout error:', error);
        const tenantKey = this.tenantService.getTenantKey();
        this.router.navigate([`/${tenantKey}`]);
      }
    });
  }

  /**
   * Get list of menu keys that have active tokens
   * Used to show logout buttons for PIN-authenticated menus
   */
  getMenusWithTokens(): string[] {
    return this.authService.getMenusWithTokens();
  }

  /**
   * Logout from a specific menu (calls backend signout)
   * @param menuKey The menu to logout from
   */
  logoutFromMenu(menuKey: string): void {
    this.authService.logoutFromMenu(menuKey).subscribe({
      next: () => {
        // Logout successful, reload the page to refresh the UI
        location.reload();
      },
      error: (error) => {
        // Even if backend logout fails, clear local state and reload
        console.error(`Error logging out from menu ${menuKey}:`, error);
        location.reload();
      }
    });
  }

  onGetUpdates() {
    window.open(this.eventOverview?.whatsappChannelUrl, '_blank');
  }

  goBack() {
    if (this.navigateTo) {
      this.router.navigate(this.tenantService.getTenantPrefixedPath(this.navigateTo));
      return;
    }
    this.router.navigate(this.tenantService.getTenantPrefixedPath('/'));
  }
}
